import { ApiError, type Api } from "../api/client.js";
import type { CardView } from "../api/types.js";
import type { AimHandlers } from "../ui/aim.js";
import { CardsView, type CardAct } from "../ui/cards.js";
import { CardDetails } from "../ui/readcard.js";
import { confirmSheet, openSheet, supportSheet, type SheetHandle } from "../ui/sheet.js";
import type { Tab } from "../ui/tabs.js";
import { cardsModel, pickCard, type CardModel } from "./cards-model.js";
import { cardAnswerFor } from "./reads.js";

/**
 * 15 Cards' controller (DCTA-111 run 2): `GET /v1/cards` + `GET /v1/accounts` for the screen;
 * Freeze and Lost or stolen are the fast path — ONE sheet, no passkey (§E.2) — `POST
 * /v1/protection/actions` `freeze_card` on the card's account; Show details is the bank's own
 * reveal (`POST /v1/cards/{id}/details`, the chat's component); Pay the bill hands over to the
 * assistant; Talk to us is the bank's number (`GET /v1/support`). A freeze is drawn from the bank's
 * re-read, never assumed; one that did not go on is loud.
 */
export interface CardsDeps {
  readonly api: Api;
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  /** The tab the screen sits under, and its back button's words ("Back to Security"). */
  readonly tab: Tab;
  readonly backLabel: string;
  readonly onBack: () => void;
  /** Pay the bill: the assistant, asked in words (the account's own action does the same). */
  readonly onPay: () => void;
  readonly onSignedOut: () => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
}

export class CardsScreen {
  readonly view: CardsView;
  readonly #d: CardsDeps;
  #raw: readonly CardView[] = [];
  #cards: readonly CardModel[] = [];
  #selected: string | undefined;
  #sheet: SheetHandle | undefined;
  #freezing = false;
  #details: CardDetails | undefined;
  /** The freeze that did not go on, kept for Try again (the customer already confirmed it). */
  #failed: CardModel | undefined;
  /** As in the chat (§6.11): a hidden page — the app switcher's snapshot — puts the details back under their mask. */
  readonly #onHidden = (): void => {
    if (document.visibilityState === "hidden") this.#details?.hide();
  };

  constructor(deps: CardsDeps) {
    this.#d = deps;
    this.view = new CardsView(
      {
        onBack: deps.onBack,
        onSelect: (id) => this.#select(id),
        onAct: (go, id) => this.#act(go, id),
        onRetry: () => (this.#failed !== undefined ? void this.#freeze(this.#failed, undefined) : void this.refresh(this.#selected)),
        onTab: deps.onTab,
        assistant: deps.assistant,
      },
      { tab: deps.tab, back: deps.backLabel },
    );
  }

  /** `selected`: a card id, or the first card linked to this account (the account's own "Card details"). */
  async refresh(selected?: string): Promise<void> {
    if (this.#cards.length === 0) this.view.loading();
    try {
      const [cards, accounts] = await Promise.all([this.#d.api.cards(), this.#d.api.accounts()]);
      this.#raw = cards.cards;
      this.#cards = cardsModel(cards.cards, accounts.accounts);
      const pick = pickCard(this.#cards, selected ?? this.#selected);
      this.#selected = pick;
      this.view.show(this.#cards, pick ?? "");
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.view.unreachable();
    }
  }

  /** Leaving: card details go back under their mask. */
  dispose(): void {
    this.#details?.hide();
    document.removeEventListener("visibilitychange", this.#onHidden);
  }

  #select(id: string): void {
    this.#details?.hide();
    this.#details = undefined;
    this.view.details(undefined);
    this.#selected = id;
    this.view.show(this.#cards, id);
  }

  #act(go: CardAct, id: string): void {
    const card = this.#cards.find((c) => c.id === id);
    if (card === undefined) return;
    switch (go) {
      case "freeze":
      case "lost":
        this.#confirm(card, go);
        return;
      case "details":
        this.#toggleDetails(id);
        return;
      case "pay":
        this.#d.onPay();
        return;
      case "talk":
        void this.#talk();
        return;
    }
  }

  #toggleDetails(id: string): void {
    if (this.#details !== undefined) {
      this.#details.hide();
      this.#details = undefined;
      this.view.details(undefined);
      return;
    }
    const raw = this.#raw.find((c) => c.card_id === id);
    if (raw === undefined) return;
    const d = new CardDetails(cardAnswerFor(raw), {
      reveal: () => this.#d.api.cardDetails(id),
      shown: () => undefined,
      hidden: () => undefined,
    });
    this.#details = d;
    this.view.details(d.element);
    document.addEventListener("visibilitychange", this.#onHidden);
  }

  /** §E.6's freeze and lost sheets — without the promise of an unfreeze or a new card the lab cannot keep yet. */
  #confirm(card: CardModel, why: "freeze" | "lost"): void {
    if (this.#sheet !== undefined || this.#freezing) return;
    const close = (): void => void this.#close();
    const name = `${card.kind === "credit" ? card.title.toLowerCase() : "debit card"} ···${card.last4}`;
    const { sheet, primary } = confirmSheet(
      why === "freeze"
        ? { icon: "i-snow", title: `Freeze ${name}?`, lead: "Card payments, cash withdrawals and online charges stop at once.", act: "Freeze card", keep: "Not now" }
        : { icon: "i-alert", title: "Lost or stolen?", lead: `We'll freeze ···${card.last4} now, so nothing new can be charged to it.`, act: "Freeze it now", keep: "Not now" },
      { onAct: () => void this.#freeze(card, primary), onKeep: close },
    );
    this.#sheet = openSheet(this.#d.host, sheet, { behind: this.view.parts, reduced: this.#d.reduced(), onDismiss: close });
  }

  async #close(): Promise<void> {
    const s = this.#sheet;
    this.#sheet = undefined;
    await s?.close();
  }

  async #freeze(card: CardModel, primary: HTMLButtonElement | undefined): Promise<void> {
    const target = card.freezeTarget;
    if (this.#freezing || target === undefined) return;
    this.#freezing = true;
    primary?.setAttribute("aria-busy", "true");
    this.#sheet?.lock(true);
    try {
      const r = await this.#d.api.freezeCards(target);
      this.#sheet?.lock(false);
      await this.#close();
      this.#failed = undefined;
      this.view.notFrozen(false);
      // The bank's own state, read back — the art turns Frozen because the bank says so.
      await this.refresh(card.id);
      this.view.done(r.result_summary);
    } catch (error) {
      this.#sheet?.lock(false);
      await this.#close();
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.#failed = card;
      this.view.notFrozen(true);
    } finally {
      this.#freezing = false;
      primary?.setAttribute("aria-busy", "false");
    }
  }

  async #talk(): Promise<void> {
    if (this.#sheet !== undefined) return;
    try {
      const contact = await this.#d.api.support();
      const close = (): void => void this.#close();
      this.#sheet = openSheet(this.#d.host, supportSheet(contact, close), { behind: this.view.parts, reduced: this.#d.reduced(), onDismiss: close });
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") this.#d.onSignedOut();
    }
  }
}
